import * as React from 'react';
import {Box, Card, CardContent, Skeleton, Typography} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import {useTranslation} from 'react-i18next';
import type {Employee} from '../../types/employee';

interface EmployeeStatCardsProps {
    readonly employees: Employee[];
    readonly loading?: boolean;
}

interface StatCardProps {
    readonly icon: React.ReactElement;
    readonly label: string;
    readonly value: number;
    readonly loading?: boolean;
}

function StatCard({icon, label, value, loading}: Readonly<StatCardProps>): React.ReactElement {
    return (
        <Card variant="outlined" sx={{borderRadius: 1, height: '100%'}}>
            <CardContent sx={{
                display: 'flex',
                alignItems: 'center',
                gap: {xs: 1, sm: 1.5},
                p: {xs: 1.25, sm: 2},
                '&:last-child': {pb: {xs: 1.25, sm: 2}},
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: {xs: 36, sm: 44},
                    height: {xs: 36, sm: 44},
                    borderRadius: '50%',
                    backgroundColor: 'success.light',
                    color: 'success.dark',
                    flexShrink: 0,
                }}>
                    {icon}
                </Box>
                <Box sx={{textAlign: 'left', minWidth: 0}}>
                    <Typography variant="body2" sx={{color: 'text.secondary'}} noWrap>
                        {label}
                    </Typography>
                    {loading ? (
                        <Skeleton variant="text" width={48} height={32}/>
                    ) : (
                        <Typography variant="h6" sx={{fontWeight: 700}} noWrap>
                            {value}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}

export default function EmployeeStatCards({employees, loading}: Readonly<EmployeeStatCardsProps>): React.ReactElement {
    const {t} = useTranslation();

    const total = employees.length;
    const active = employees.filter((e) => (e.status || '').toLowerCase() === 'active').length;
    const archived = total - active;

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: {xs: 'repeat(3, 1fr)', sm: 'repeat(3, 1fr)'},
            gap: 2,
        }}>
            <StatCard icon={<GroupsOutlinedIcon/>} label={t('employee.stats.total')} value={total} loading={loading}/>
            <StatCard icon={<PersonOutlineOutlinedIcon/>} label={t('employee.stats.active')} value={active}
                      loading={loading}/>
            <StatCard icon={<Inventory2OutlinedIcon/>} label={t('employee.stats.archived')} value={archived}
                      loading={loading}/>
        </Box>
    );
}
